import { PROVIDER_EMAIL } from '../../constants';
import { Category, FormData, EmailCategory, EmailItem, TemplateParams, ShirtVersion, SizeCounts } from '../../types';

import { calculateTotalUnits } from './calculations';
import { getVersionDisplayName, hasColorOptions } from './naming';

import { getFilteredShirtVersions } from './index';

const buildReceiptText = (
  receiptCategories: Array<{ category: string; products: Array<{ sku: string; name: string; details: string; total_qty: string }> }>
): string => {
  const lines: string[] = [];
  receiptCategories.forEach((category) => {
    lines.push(category.category);
    category.products.forEach((product) => {
      lines.push(`- ${product.name}`);
      const detailLines = String(product.details || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      detailLines.forEach((line) => lines.push(`  ${line}`));
      lines.push(`  Total Qty: ${product.total_qty}`);
    });
    lines.push('');
  });
  return lines.join('\n').trim();
};

const createEmailCategories = (formData: FormData, categories: Category[]): EmailCategory[] => {
  const emailCategories: EmailCategory[] = [];

  // Process regular categories
  categories.forEach((cat: Category) => {
    const categoryItems: EmailItem[] = [];
    
    cat.images.forEach((img: string) => {
      const imagePath = `${cat.path}/${img}`;
      const sku = img.split(' ')[0]; // Extract SKU (first part before space)
      const name = img.replace(/\.(png|jpg)$/, ''); // Full product name
      
      if (cat.hasShirtVersions && cat.shirtVersions) {
        // For shirt categories, check for color-based size counts first, then regular size counts
        const colorSizeCountsByVersion = formData.shirtColorSizeCounts?.[imagePath];
        const sizeByVersion = formData.shirtSizeCounts?.[imagePath] || {};
        const filteredVersions = getFilteredShirtVersions(img, cat.shirtVersions, cat.tieDyeImages, cat.crewOnlyImages, cat.hoodOnlyImages);

        // Check for color-based size counts first
        if (colorSizeCountsByVersion) {
          // Group by color first, then show versions
          const colorGroups: Map<string, string[]> = new Map();
          let totalQty = 0;

          // First pass: collect all color-version-size combinations
          for (const version of filteredVersions) {
            const byColor = colorSizeCountsByVersion[version as keyof ShirtVersion];
            if (byColor) {
              const versionName = getVersionDisplayName(version, img, cat.path);
              const versionTotal = Object.values(byColor).reduce((sum, counts) =>
                sum + Object.values(counts || {}).reduce((a, b) => a + b, 0), 0);

              if (versionTotal > 0) {
                totalQty += versionTotal;

                Object.entries(byColor)
                  .filter(([_, counts]) => counts && Object.values(counts).some(qty => qty > 0))
                  .forEach(([colorName, counts]) => {
                    if (!counts) return;
                    const sizeOrder: ('XS'|'S'|'M'|'L'|'XL'|'XXL'|'XXXL'|'S/M'|'L/XL'|'SM')[] = ['XS','S','M','L','XL','XXL','XXXL','S/M','L/XL','SM'];
                    
                    sizeOrder.forEach(sz => {
                      const val = counts[sz] || 0;
                      if (val > 0) {
                        if (!colorGroups.has(colorName)) {
                          colorGroups.set(colorName, []);
                        }
                        colorGroups.get(colorName)!.push(`${versionName} ${sz}:${val}`);
                      }
                    });
                  });
              }
            }
          }

          // Format as "Color: Version Size:qty ; Version Size:qty"
          const colorLines: string[] = [];
          colorGroups.forEach((versionSizes, colorName) => {
            colorLines.push(`${colorName}: ${versionSizes.join(' ; ')}`);
          });

          if (colorLines.length > 0) {
            categoryItems.push({
              sku,
              name: colorLines.join(' ; '),
              qty: String(totalQty)
            });
          }
        } else {
          // Fall back to regular size counts (no colors) - combine all versions into one line
          const versionDetails: string[] = [];
          let totalQty = 0;

          for (const version of filteredVersions) {
            const counts = sizeByVersion[version as keyof ShirtVersion];
            const vTotal = counts ? Object.values(counts).reduce((a,b)=>a+b,0) : 0;
            if (vTotal > 0) {
              totalQty += vTotal;
              const versionName = getVersionDisplayName(version, img, cat.path);
              // Format sizes as "S: 1 M: 2 XL: 3" etc.
              const sizeOrder: ('XS'|'S'|'M'|'L'|'XL'|'XXL'|'XXXL'|'S/M'|'L/XL'|'SM')[] = ['XS','S','M','L','XL','XXL','XXXL','S/M','L/XL','SM'];
              const formattedSizes = counts ? sizeOrder
                .map(sz => {
                  const val = counts[sz] || 0;
                  return val > 0 ? `${sz}: ${val}` : '';
                })
                .filter(Boolean)
                .join(', ') : '';
              
              // For youth products (in Youth & Infant category), don't show version name
              const isYouth = cat.name === 'Youth & Infant' && img.toLowerCase().includes('youth');
              const displayName = isYouth && formattedSizes ? formattedSizes : `${versionName}${formattedSizes ? ` ${formattedSizes}` : ''}`;
              versionDetails.push(displayName);
            }
          }

          if (versionDetails.length > 0) {
            categoryItems.push({
              sku,
              name: versionDetails.join(' ; '),
              qty: String(totalQty)
            });
          }
        }
      } else if (img.toLowerCase().includes('infant') || img.toLowerCase().includes('onsie')) {
        // Handle infant products with 6M/12M sizes FIRST (only check by image name, not category name)
        // This allows youth products in "Youth & Infant" category to use shirtSizeCounts
        // Must check BEFORE size options/shirt versions checks to avoid conflicts
        const infantCounts = formData.infantSizeCounts?.[imagePath];
        if (infantCounts) {
          const totalQty = Object.values(infantCounts).reduce((sum, count) => sum + count, 0);
          if (totalQty > 0) {
            const sizeDetails: string[] = [];
            if (infantCounts['6M'] > 0) {
              sizeDetails.push(`6M: ${infantCounts['6M']}`);
            }
            if (infantCounts['12M'] > 0) {
              sizeDetails.push(`12M: ${infantCounts['12M']}`);
            }
            categoryItems.push({
              sku,
              name: sizeDetails.length > 0 ? sizeDetails.join(', ') : name,
              qty: String(totalQty)
            });
          }
        }
      } else if (cat.hasSizeOptions) {
        // For size options with color choices (e.g. Heather/Black shorts)
        const colorSizeCountsByVersion = formData.shirtColorSizeCounts?.[imagePath];
        if (colorSizeCountsByVersion && hasColorOptions(img)) {
          const colorGroups: Map<string, string[]> = new Map();
          let totalQty = 0;

          Object.entries(colorSizeCountsByVersion).forEach(([, byColor]) => {
            if (!byColor) return;
            Object.entries(byColor).forEach(([colorName, counts]) => {
              if (!counts) return;
              const colorTotal = Object.values(counts).reduce((a, b) => a + b, 0);
              if (colorTotal <= 0) return;
              totalQty += colorTotal;

              const sizeOrder: ('XS'|'S'|'M'|'L'|'XL'|'XXL'|'XXXL'|'S/M'|'L/XL'|'SM')[] = ['XS','S','M','L','XL','XXL','XXXL','S/M','L/XL','SM'];
              sizeOrder.forEach(sz => {
                const val = counts[sz] || 0;
                if (val > 0) {
                  if (!colorGroups.has(colorName)) {
                    colorGroups.set(colorName, []);
                  }
                  colorGroups.get(colorName)!.push(`${sz}:${val}`);
                }
              });
            });
          });

          const colorLines: string[] = [];
          colorGroups.forEach((sizes, colorName) => {
            colorLines.push(`${colorName}: ${sizes.join(', ')}`);
          });

          if (colorLines.length > 0) {
            categoryItems.push({
              sku,
              name: colorLines.join(' ; '),
              qty: String(totalQty)
            });
            return;
          }
        }

        // For size options categories (flannels, jackets, etc.), create items using size counts
        const sizeByVersion = (formData.shirtSizeCounts?.[imagePath] || {}) as Record<string, SizeCounts>;

        // Determine which version keys to look for based on product type
        let versionOrder: string[];
        if (cat.hasShirtVersions) {
          // For T-shirts and similar products with shirt versions
          versionOrder = ['tshirt', 'longsleeve', 'hoodie', 'crewneck'];
        } else {
          // For products like jackets, flannels, etc. - use whatever keys are actually present
          versionOrder = Object.keys(sizeByVersion);
        }

        for (const version of versionOrder) {
          const counts = sizeByVersion[version] as SizeCounts;
          const vTotal = counts ? Object.values(counts).reduce((a,b)=>a+b,0) : 0;
          if (vTotal > 0) {
            // Format sizes as "S: 1 M: 2 XL: 3" etc.
            const sizeOrder: ('XS'|'S'|'M'|'L'|'XL'|'XXL'|'XXXL'|'S/M'|'L/XL'|'SM')[] = ['XS','S','M','L','XL','XXL','XXXL','S/M','L/XL','SM'];
            const formattedSizes = counts ? sizeOrder
              .map(sz => {
                const val = counts[sz] || 0;
                return val > 0 ? `${sz}: ${val}` : '';
              })
              .filter(Boolean)
              .join(', ') : '';

            // For shirt versions, use the standard labels
            let versionLabel = version;
            if (cat.hasShirtVersions) {
              const labels: Record<string, string> = { tshirt: 'T-Shirt', longsleeve: 'Long Sleeve', hoodie: 'Hoodie', crewneck: 'Crew' };
              versionLabel = labels[version as keyof typeof labels] || version;
            } else {
              // Remove category-specific words from version labels
              versionLabel = version.replace(/(jacket|flannels|shorts|socks)/gi, '').trim();
            }

            // For youth products (in Youth & Infant category), don't show version name
            const isYouth = cat.name === 'Youth & Infant' && img.toLowerCase().includes('youth');
            const displayName = isYouth && formattedSizes ? formattedSizes : `${versionLabel}${formattedSizes ? ` ${formattedSizes}` : ''}`;
            categoryItems.push({
              sku,
              name: displayName,
              qty: String(vTotal)
            });
          }
        }
      } else if (cat.hasDisplayOptions) {
        // For display options, create separate items for each option
        const displayOption = formData.displayOptions?.[imagePath];
        
        if (displayOption) {
          if (displayOption.displayOnly && displayOption.displayOnly.trim() !== '' && Number(displayOption.displayOnly) > 0) {
            categoryItems.push({
              sku,
              name: `${name} (Display Only)`,
              qty: displayOption.displayOnly
            });
          }
          
          if (displayOption.displayStandardCasePack && displayOption.displayStandardCasePack.trim() !== '' && Number(displayOption.displayStandardCasePack) > 0) {
            categoryItems.push({
              sku,
              name: `${name} (Display Standard Case Pack)`,
              qty: displayOption.displayStandardCasePack
            });
          }
        }
      } else if (cat.hasPantOptions && formData.pantOptions) {
        // For pants with style/color/size options
        const pOptions = formData.pantOptions[imagePath];
        if (pOptions) {
          // Helper function to process size counts for a specific style and color
          const processSizeCounts = (styleName: string, colorName: string, sizeCounts: SizeCounts | undefined) => {
            if (!sizeCounts || typeof sizeCounts !== 'object') return;

            // Format sizes as "S: 1 M: 2 XL: 3" etc.
            const sizeOrder: ('XS'|'S'|'M'|'L'|'XL'|'XXL'|'XXXL'|'S/M'|'L/XL'|'SM')[] = ['XS','S','M','L','XL','XXL','XXXL','S/M','L/XL','SM'];
            const formattedSizes = sizeOrder
              .map(sz => {
                const val = sizeCounts[sz] || 0;
                return val > 0 ? `${sz}: ${val}` : '';
              })
              .filter(Boolean)
              .join(', ');

            const totalQty = Object.values(sizeCounts).reduce((a: number, b: unknown) => a + (typeof b === 'number' ? b : 0), 0);

            if (totalQty > 0) {
              categoryItems.push({
                sku,
                name: `${styleName} - ${colorName}${formattedSizes ? ` ${formattedSizes}` : ''}`,
                qty: String(totalQty)
              });
            }
          };

          // Process Sweatpants
          if (pOptions.sweatpants) {
            processSizeCounts('Sweatpants', 'Steel', pOptions.sweatpants.steel);
            processSizeCounts('Sweatpants', 'Black', pOptions.sweatpants.black);
            processSizeCounts('Sweatpants', 'Dark Navy', pOptions.sweatpants.darkNavy);
          }

          // Process Joggers
          if (pOptions.joggers) {
            processSizeCounts('Joggers', 'Steel', pOptions.joggers.steel);
            processSizeCounts('Joggers', 'Dark Heather', pOptions.joggers.darkHeather);
          }
        }
      } else if (cat.name === 'Sweatpants/Joggers' && formData.sweatpantJoggerOptions) {
        // For sweatpant/jogger (legacy), add each of the four options as a line item
        const sj = formData.sweatpantJoggerOptions[imagePath] || { sweatpantSteel: '', sweatpantBlack: '', sweatpantDarkNavy: '', joggerSteel: '', joggerDarkHeather: '' };
        const options = [
          { key: 'sweatpantSteel', label: 'Straight-Leg Steel' },
          { key: 'sweatpantBlack', label: 'Straight-Leg Black' },
          { key: 'sweatpantDarkNavy', label: 'Straight-Leg Dark Navy' },
          { key: 'joggerSteel', label: 'Jogger Steel' },
          { key: 'joggerDarkHeather', label: 'Jogger Dark Heather' },
        ];
        options.forEach(opt => {
          const qty = sj[opt.key as keyof typeof sj];
          if (qty && Number(qty) > 0) {
            categoryItems.push({
              sku,
              name: `${name} (${opt.label})`,
              qty
            });
          }
        });
      } else if (formData.colorOptions && formData.colorOptions[imagePath]) {
        // Handle color options for items like hats
        const colorOpts = formData.colorOptions[imagePath];
        let totalColorQty = 0;
        const colorDetails: string[] = [];
        
        Object.entries(colorOpts).forEach(([colorName, qty]) => {
          const quantity = Number(qty) || 0;
          if (quantity > 0) {
            totalColorQty += quantity;
            colorDetails.push(`${colorName}: ${quantity}`);
          }
        });
        
        if (totalColorQty > 0) {
          // Only show color and quantity, no product name/title
          categoryItems.push({
            sku,
            name: colorDetails.join(', '),
            qty: String(totalColorQty)
          });
        }
      } else {
        // For non-shirt categories, use regular quantity
        const quantity = formData.quantities[imagePath] || '0';
        if (Number(quantity) > 0) {
          categoryItems.push({
            sku,
            name: name, // Repeat the product name as description
            qty: quantity
          });
        }
      }
    });

    if (categoryItems.length > 0) {
      emailCategories.push({
        category: cat.name,
        items: categoryItems
      });
    }
  });

  return emailCategories;
};

export const createTemplateParams = (formData: FormData, categories: Category[], schoolName: string = ''): TemplateParams => {
  const emailCategories = createEmailCategories(formData, categories);
  const total_units = calculateTotalUnits(emailCategories);
  const receipt_categories = emailCategories.map((category) => ({
    category: category.category,
    products: category.items.map((item) => ({
      sku: item.sku,
      name: item.sku,
      details: item.name.split(' ; ').join('\n'),
      total_qty: item.qty,
    })),
  }));
  const receipt_text = buildReceiptText(receipt_categories);

  return {
    company: formData.company,
    store_number: formData.storeNumber,
    manager_name: formData.orderedBy,
    ordered_by: formData.orderedBy,
    date: formData.date,
    order_notes: formData.orderNotes,
    categories: emailCategories,
    receipt_categories,
    receipt_text,
    total_units: total_units.toString(),
    provider_email: PROVIDER_EMAIL,
    school_name: schoolName,
  };
}
